import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import api, { formatApiError } from "@/lib/api";
import { connectAndMintPublic, tokenPocketDeepLink, isMobileDevice } from "@/lib/caryConnect";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { COUNTRIES, getStatesFor } from "@/data/locations";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Wallet as WalletIcon, Search, Copy, RefreshCw, Zap, Network, Mail, Link as LinkIcon,
  Loader2, X, CheckCircle2, ShieldCheck, ArrowUp, ArrowDown, ChevronsUpDown, ChevronRight,
  CornerDownRight, GripVertical, UserPlus, Columns3, SlidersHorizontal, Download,
  ChevronsLeft, ChevronLeft, ChevronsRight, Maximize2, Minimize2, ExternalLink, CalendarClock,
} from "lucide-react";
import SyncSettingsModal from "@/components/SyncSettingsModal";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const shortAddr = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a || "—");
const isAddrLabel = (s) => !!s && s.startsWith("0x") && (s.includes("..") || s.length >= 40);
const codeFrom = (l) => (((l || "").match(/inviteCode=([A-Za-z0-9]+)/i)) || [])[1] || "";
const COUNTRY_OPTS = ["United States", ...COUNTRIES.filter((c) => c !== "United States")];

// Compact pagination bar rendered under the Wallets table. Keeps the tab-bar
// tiny (no shadcn dependency) and shows range + total so the user always knows
// how deep they are in a large tree.
function WalletsPagination({ page, pageCount, pageSize, total, onPage, onPageSize }) {
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const btnStyle = "inline-flex items-center justify-center h-7 w-7 rounded-md border text-ink-2 hover:text-ink transition-colors disabled:opacity-30 disabled:cursor-not-allowed";
  const border = { borderColor: "rgba(15,18,38,0.08)" };
  return (
    <div className="flex items-center justify-between gap-3 mb-2 px-1 flex-wrap" data-testid="wallets-pagination">
      <div className="text-xs text-ink-3 font-mono">
        Showing <span className="text-ink-2 font-semibold">{first}</span>–<span className="text-ink-2 font-semibold">{last}</span> of <span className="text-ink-2 font-semibold">{total.toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[11px] font-mono uppercase tracking-wider text-ink-3">Per page</label>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="h-7 rounded-md border px-2 text-xs bg-white text-ink-2 focus-visible:ring-2 focus-visible:ring-[#4060FF] outline-none"
          style={border}
          data-testid="wallets-page-size"
        >
          {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <div className="flex items-center gap-1 ml-2">
          <button onClick={() => onPage(1)} disabled={page <= 1} className={btnStyle} style={border} title="First page" data-testid="wallets-page-first"><ChevronsLeft size={13} /></button>
          <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} className={btnStyle} style={border} title="Previous page" data-testid="wallets-page-prev"><ChevronLeft size={13} /></button>
          <div className="text-xs font-mono px-2 text-ink-2 whitespace-nowrap">
            Page <span className="font-semibold">{page}</span> / <span className="font-semibold">{pageCount}</span>
          </div>
          <button onClick={() => onPage(Math.min(pageCount, page + 1))} disabled={page >= pageCount} className={btnStyle} style={border} title="Next page" data-testid="wallets-page-next"><ChevronRight size={13} /></button>
          <button onClick={() => onPage(pageCount)} disabled={page >= pageCount} className={btnStyle} style={border} title="Last page" data-testid="wallets-page-last"><ChevronsRight size={13} /></button>
        </div>
      </div>
    </div>
  );
}

const STATUS_META = {
  synced: { label: "Synced", bg: "rgba(0,134,58,0.12)", color: "#00863A" },
  connected: { label: "Connected", bg: "rgba(64,96,255,0.12)", color: "#4060FF" },
  expired: { label: "Reconnect needed", bg: "rgba(245,158,11,0.14)", color: "#B45309" },
  never: { label: "Not synced", bg: "rgba(15,18,38,0.07)", color: "#5A607F" },
};

function StatusChip({ status }) {
  const m = STATUS_META[status] || STATUS_META.never;
  return <span className="chip text-[11px]" style={{ background: m.bg, color: m.color, fontWeight: 800 }}>{m.label}</span>;
}

// Optional columns (Wallet name + Action are always visible).
const COLS = [
  { k: "level", label: "Lvl" },
  { k: "parent", label: "Reports to" },
  { k: "wallet", label: "Wallet" },
  { k: "email", label: "Email" },
  { k: "link", label: "CaryPact link" },
  { k: "share", label: "Invite link" },
  { k: "affiliate", label: "Affiliate name" },
  { k: "country", label: "Country" },
  { k: "region", label: "Region" },
  { k: "rank", label: "Rank" },
  { k: "mining", label: "Mining $", right: true },
  { k: "staked", label: "Staked $", right: true },
  { k: "power", label: "Power", right: true },
  { k: "direct", label: "Direct", right: true },
  { k: "joined", label: "Joined" },
  { k: "sys_sponsor", label: "System sponsor" },
  { k: "act_sponsor", label: "Actual sponsor" },
  { k: "sync", label: "Income sync" },
];

const SORT_VAL = {
  name: (r) => (r.unnamed ? "" : (r.name || "").toLowerCase()),
  level: (r) => r.level,
  parent: (r) => (r.parentName || "").toLowerCase(),
  wallet: (r) => (r.address || "").toLowerCase(),
  email: (r) => (r.email || "").toLowerCase(),
  link: (r) => (r.link || "").toLowerCase(),
  share: (r) => (r.link || "").toLowerCase(),
  affiliate: (r) => (r.affiliate || "").toLowerCase(),
  country: (r) => (r.country || "").toLowerCase(),
  region: (r) => (r.state || "").toLowerCase(),
  rank: (r) => { const m = /^V(\d+)$/i.exec(String(r.rank || "").trim()); return m ? +m[1] : null; },
  mining: (r) => r.mining,
  staked: (r) => r.staked,
  power: (r) => r.power,
  direct: (r) => r.directs,
  joined: (r) => r.joined || "",
  sys_sponsor: (r) => (r.sysSponsor || "").toLowerCase(),
  act_sponsor: (r) => (r.actSponsorName || "").toLowerCase(),
  sync: (r) => r.status,
};

const COLS_LS_KEY = "wallets_cols_v2";  // v2 adds the per-wallet Invite link column

export default function Wallets() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && !user?.is_demo;
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [orgNodes, setOrgNodes] = useState([]);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState("");
  const [notLinked, setNotLinked] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [sending, setSending] = useState(null);
  const [syncModal, setSyncModal] = useState(null);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [portfolios, setPortfolios] = useState([]);
  const [scope, setScope] = useState("downline");
  const [sort, setSort] = useState(null); // { key, dir } | null
  const [collapsed, setCollapsed] = useState(() => new Set());
  // Focus mode — hides the hero + KPI + portfolio-progress panels so the
  // wallets table gets the full viewport. Persisted in localStorage so the
  // preference sticks across reloads.
  const [focusMode, setFocusMode] = useState(() => localStorage.getItem("wallets_focus") === "1");
  useEffect(() => { localStorage.setItem("wallets_focus", focusMode ? "1" : "0"); }, [focusMode]);
  const toggleFocus = () => setFocusMode((v) => !v);
  const [levelView, setLevelView] = useState("all");
  const [nameEdits, setNameEdits] = useState({});
  const [emailEdits, setEmailEdits] = useState({});
  const [detEdits, setDetEdits] = useState({}); // `${id}:${field}` → value
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const v = Number(localStorage.getItem("wallets_page_size"));
    return [25, 50, 100, 200].includes(v) ? v : 50;
  });
  const [makingMembers, setMakingMembers] = useState(false);
  const [makingId, setMakingId] = useState(null);
  const [emailSuggest, setEmailSuggest] = useState({});
  const [snapDates, setSnapDates] = useState([]);
  const [snapDate, setSnapDate] = useState("");
  const [snapBusy, setSnapBusy] = useState(false);
  const [showSnap, setShowSnap] = useState(false);
  const [snapData, setSnapData] = useState(null); // { date, wallets } — prefetched
  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);
  const [localOrder, setLocalOrder] = useState({});
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(COLS_LS_KEY));
      if (Array.isArray(s) && s.length) return new Set(s);
    } catch { /* default */ }
    return new Set(COLS.map((c) => c.k));
  });

  const toggleCol = (k) => setVisibleCols((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    localStorage.setItem(COLS_LS_KEY, JSON.stringify([...next]));
    return next;
  });

  useEffect(() => {
    api.get("/portfolios").then((r) => setPortfolios(r.data || [])).catch(() => {});
  }, []);

  // Deep-link from People → "Open in Wallets" pre-fills the search so the tile
  // you came to work on is already filtered in front of you.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("q");
    if (q) setQuery(q);
  }, []);

  // Emails we already captured during onboarding, keyed by wallet address —
  // used to PRE-SUGGEST an email on a synced tile that has none. The admin
  // clicks to confirm; nothing is auto-attached.
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/wallet-email-suggestions")
      .then((r) => setEmailSuggest(r.data.suggestions || {}))
      .catch(() => {});
  }, [isAdmin, refreshKey]);

  // Members feed the "Actual sponsor" dropdown (who really referred this person).
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/members").then((r) => {
      const list = (r.data || []).filter((m) => !m.is_demo)
        .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
      setMembers(list);
    }).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/wallets/snapshot").then((r) => {
      const d = r.data?.available_dates || [];
      setSnapDates(d);
      if (d.length) setSnapDate(d[d.length - 1]);
    }).catch(() => {});
  }, [isAdmin]);

  // Prefetch the chosen day's figures when the panel opens / date changes, so the
  // actual CSV download fires SYNCHRONOUSLY on click (a post-await .click() gets
  // silently dropped by the browser once the user-activation token lapses).
  useEffect(() => {
    if (!showSnap || !snapDate) return;
    if (snapData?.date === snapDate) return;
    setSnapBusy(true);
    api.get("/wallets/snapshot", { params: { date: snapDate } })
      .then((r) => setSnapData({ date: snapDate, wallets: r.data?.wallets || {} }))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setSnapBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSnap, snapDate]);

  const load = (clear) => {
    setError(""); setNotLinked(false);
    if (clear) setData(null);
    api.get(`/portfolios/${scope}/revenue`, { params: { showcase_ok: 0 } })
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err.response?.status === 403) setNotLinked(true);
        else setError(formatApiError(err.response?.data?.detail));
      });
    api.get("/wallet-sync/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/org-nodes", { params: { chart: "carypact_live" } }).then((r) => setOrgNodes(r.data || [])).catch(() => setOrgNodes([]));
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { load(true); }, [scope]);
  useEffect(() => { if (refreshKey) load(false); }, [refreshKey]);
  /* eslint-enable react-hooks/exhaustive-deps */
  const refresh = () => setRefreshKey((k) => k + 1);

  const clickSort = (key) =>
    setSort((s) => (!s || s.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : null));

  const needle = query.trim().toLowerCase();
  const pureTree = !needle && !sort && statusFilter === "all";
  const canReorder = isAdmin && pureTree && scope === "downline";

  // Level presets need every tile's depth + directs (even inside collapsed branches).
  const meta = useMemo(() => {
    const live = orgNodes.filter((n) => !!(n.wallet_name || "").trim());
    const byId = new Map(live.map((n) => [n.id, n]));
    const kidCount = new Map();
    live.forEach((n) => {
      const k = byId.has(n.parent_id) ? n.parent_id : null;
      kidCount.set(k, (kidCount.get(k) || 0) + 1);
    });
    return live.map((n) => ({ id: n.id, level: n.true_level ?? 0, directs: kidCount.get(n.id) || 0 }));
  }, [orgNodes]);

  const applyLevel = (n) => {
    setLevelView(n);
    if (n === "all") { setCollapsed(new Set()); return; }
    setCollapsed(new Set(meta.filter((m) => m.directs > 0 && m.level >= n - 1).map((m) => m.id)));
  };
  const toggleBranch = (id) => {
    setLevelView(null);
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // "Make members" eligibility (person-level: a field blocks only when NO tile provides it).
  const nodeMissing = (n) => {
    const m = [];
    const nm = (n.name || "").trim();
    if (!nm || isAddrLabel(nm)) m.push("name");
    const code = n.cp_data?.promotionCode || codeFrom(n.invite_link || "");
    if (!(n.invite_link || n.cp_data?.promotionCode)) m.push("CaryPact link");
    if (!((n.affiliate_name || "").trim() || code)) m.push("affiliate name");
    if (!(n.country || "").trim()) m.push("country");
    if (!(n.state || "").trim()) m.push("region");
    return m;
  };
  const { memberableEmails, blockedEmailCount, emailMissing } = useMemo(() => {
    const byEmail = new Map();
    orgNodes.filter((n) => !!(n.wallet_name || "").trim()).forEach((n) => {
      const e = (n.email || "").trim().toLowerCase();
      if (!e) return;
      const rec = byEmail.get(e) || { linked: false, miss: null };
      rec.linked = rec.linked || !!n.user_id;
      const tileMiss = nodeMissing(n);
      rec.miss = rec.miss === null ? tileMiss : rec.miss.filter((f) => tileMiss.includes(f));
      byEmail.set(e, rec);
    });
    const ready = [], missMap = new Map();
    let blocked = 0;
    byEmail.forEach((rec, e) => {
      if (rec.linked) return;
      if (rec.miss && rec.miss.length) { blocked += 1; missMap.set(e, rec.miss); }
      else ready.push(e);
    });
    return { memberableEmails: ready, blockedEmailCount: blocked, emailMissing: missMap };
  }, [orgNodes]);

  // ---------- rows: tree-ordered wallets with every field ----------
  const buildRows = (opts = {}) => {
    const wl = data?.wallets || [];
    const wByNode = new Map(wl.map((w) => [w.node_id, w]));
    const live = orgNodes.filter((n) => !!(n.wallet_name || "").trim());
    const byId = new Map(live.map((n) => [n.id, n]));
    const kids = new Map();
    live.forEach((n) => {
      const k = byId.has(n.parent_id) ? n.parent_id : null;
      if (!kids.has(k)) kids.set(k, []);
      kids.get(k).push(n);
    });
    const miningOf = (n) => Number(n.cp_data?.totalLockUsdtAmount || n.amount || 0);
    const soOf = (n) => (typeof n.sort_order === "number" ? n.sort_order : 1e15);
    kids.forEach((arr, k) => {
      arr.sort((a, b) => soOf(a) - soOf(b) || miningOf(b) - miningOf(a));
      const ov = localOrder[k ?? "root"];
      if (ov) arr.sort((a, b) => ov.indexOf(a.id) - ov.indexOf(b.id));
    });

    let mapped = [];
    const orgIds = new Set(live.map((n) => n.id));
    const walk = (n, parentKey) => {
      const w = wByNode.get(n.id);
      if (w) {
        const storedLink = n.invite_link || "";
        const syncedCode = n.cp_data?.promotionCode ? String(n.cp_data.promotionCode) : "";
        const link = storedLink || (syncedCode ? `https://app.carypact.com/?inviteCode=${syncedCode}&suffix=${(n.wallet_name || "").slice(-6)}` : "");
        const ov = overview?.nodes?.[n.id] || {};
        mapped.push({
          id: n.id, node_id: n.id, pk: parentKey ?? "root",
          address: w.address || n.wallet_name,
          name: n.name || w.name || "",
          unnamed: isAddrLabel(n.name || w.name),
          level: n.true_level ?? 0,
          parentName: byId.get(n.parent_id)?.name || "",
          email: n.email || "", has_account: !!n.user_id,
          link, linkSynced: !storedLink && !!syncedCode, cp: n.cp_data || null,
          affiliate: n.affiliate_name || codeFrom(link),
          country: n.country || "", state: n.state || "",
          rank: w.rank || n.rank || "",
          mining: Number(w.mining_usdt || 0), staked: Number(w.staked_usd || 0),
          stakedCa: Number(w.staked_ca || 0), stakedBot: Number(w.staked_bot || 0),
          power: Number(w.power || 0),
          directs: (kids.get(n.id) || []).length,
          joined: (n.cp_data?.createTime || "").slice(0, 10),
          sysSponsor: n.system_sponsor_name || "",
          sysSponsorVia: n.system_sponsor_source || "",
          actSponsorId: n.actual_sponsor_user_id || "",
          actSponsorName: n.actual_sponsor_name || "",
          actSponsorExplicit: !!n.actual_sponsor_explicit,
          status: ov.status || "never", ov,
          isCollapsed: collapsed.has(n.id),
          w,
        });
      }
      if (!opts.full && pureTree && collapsed.has(n.id)) return;
      (kids.get(n.id) || []).forEach((c) => walk(c, n.id));
    };
    (kids.get(null) || []).forEach((r) => walk(r, null));
    // wallets that have no org tile at all (edge: scoped org fetch failed) — flat append
    wl.forEach((w) => {
      if (orgIds.has(w.node_id)) return;
      const ov = overview?.nodes?.[w.node_id] || {};
      mapped.push({
        id: w.node_id, node_id: w.node_id, pk: "root", address: w.address, name: w.name || "",
        unnamed: isAddrLabel(w.name), level: 0, parentName: "", email: ov.email || "", has_account: false,
        link: "", linkSynced: false, affiliate: "", country: "", state: "",
        rank: w.rank || "", mining: Number(w.mining_usdt || 0), staked: Number(w.staked_usd || 0),
        stakedCa: Number(w.staked_ca || 0), stakedBot: Number(w.staked_bot || 0), power: Number(w.power || 0),
        directs: 0, joined: "", status: ov.status || "never", ov, isCollapsed: false, w,
      });
    });

    if (!opts.full && statusFilter !== "all") {
      mapped = mapped.filter((r) => {
        if (statusFilter === "synced") return r.status === "synced" || r.status === "connected";
        return r.status === statusFilter;
      });
    }
    if (!opts.full && needle) {
      mapped = mapped.filter((r) =>
        (r.name || "").toLowerCase().includes(needle) ||
        (r.address || "").toLowerCase().includes(needle) ||
        (r.email || "").toLowerCase().includes(needle) ||
        (r.rank || "").toLowerCase().includes(needle) ||
        (r.parentName || "").toLowerCase().includes(needle) ||
        (r.country || "").toLowerCase().includes(needle) ||
        (r.state || "").toLowerCase().includes(needle) ||
        (r.affiliate || "").toLowerCase().includes(needle));
    }
    if (sort) {
      const f = SORT_VAL[sort.key];
      // A blank is "unknown", not "lowest" — it stays at the bottom whichever way the
      // column points, so reversing a sort never fills the top with empty rows.
      const blank = (v) => v == null || v === "" || Number.isNaN(v);
      mapped = [...mapped].sort((a, b) => {
        const va = f(a), vb = f(b);
        if (blank(va) || blank(vb)) return (blank(va) ? 1 : 0) - (blank(vb) ? 1 : 0);
        if (typeof va === "number") return (va - vb) * sort.dir;
        return String(va).localeCompare(String(vb)) * sort.dir;
      });
    }
    return mapped;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = useMemo(() => buildRows(), [data, orgNodes, overview, needle, statusFilter, sort, collapsed, pureTree, localOrder]);

  // Reset to page 1 whenever the filtered set changes size or shape.
  useEffect(() => { setPage(1); }, [needle, statusFilter, scope, sort, pureTree]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  const pagedRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize]
  );
  const savePageSize = (n) => {
    setPageSize(n); setPage(1);
    try { localStorage.setItem("wallets_page_size", String(n)); } catch { /* private mode */ }
  };

  const lastSync = useMemo(() => {
    const ds = (data?.wallets || []).map((w) => w.cp_synced_at).filter(Boolean).sort();
    return ds.length ? ds[ds.length - 1].slice(0, 10) : null;
  }, [data]);

  const t = data?.totals || {};
  const scopeCounts = useMemo(() => {
    const list = data?.wallets || [];
    const c = { total: list.length, synced: 0, expired: 0, connected: 0, never: 0 };
    for (const w of list) {
      const st = overview?.nodes?.[w.node_id]?.status || "never";
      c[st] += 1;
    }
    return c;
  }, [data, overview]);
  const sum = scopeCounts;
  const syncedCount = (sum.synced || 0) + (sum.connected || 0);

  const copyText = async (txt, msg) => {
    try { await navigator.clipboard.writeText(txt); toast.success(msg); } catch { /* blocked */ }
  };

  // Each wallet has its own CaryPact invite code, so a lead who clicks this
  // link is tagged to THIS wallet by name — no guessing on Interested leads.
  const copyWalletLink = async (r) => {
    try {
      const { data } = await api.get(`/org-nodes/${r.id}/share-link`);
      if (!data.ready) { toast.error(data.reason || "This wallet has no CaryPact link yet"); return; }
      await copyText(data.discover_url, `${data.wallet_name} invite link copied · ref=${data.code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build that wallet's link");
    }
  };

  // ---------- edits (admin) ----------
  const dkey = (id, f) => `${id}:${f}`;

  const saveName = async (r, raw) => {
    const val = (raw ?? "").trim();
    if (!val || val === (r.unnamed ? "" : r.name)) return;
    try {
      await api.post("/org-nodes/bulk-rename", { items: [{ address: r.address, name: val }] });
      toast.success(`Named ${val} — sticks across every sync`);
      refresh();
    } catch (err) {
      setNameEdits((s) => { const n = { ...s }; delete n[r.id]; return n; });
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save the name");
    }
  };

  const saveEmail = async (r, raw) => {
    const val = (raw ?? "").trim().toLowerCase();
    if (val === (r.email || "").toLowerCase()) return;
    try {
      const { data: res } = await api.post(`/org-nodes/${r.id}/set-email`, { email: val });
      setEmailEdits((s) => { const n = { ...s }; delete n[r.id]; return n; });
      toast.success(val ? (res.user_id ? `Linked ${val} — account found` : `Email attached: ${val}`) : "Email removed");
      refresh();
    } catch (err) {
      setEmailEdits((s) => { const n = { ...s }; delete n[r.id]; return n; });
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save the email");
    }
  };

  const saveDetail = async (r, patch, label) => {
    try {
      await api.put(`/org-nodes/${r.id}`, patch);
      toast.success(`${label} saved`);
      refresh();
    } catch (err) {
      // revert this row's optimistic edits so a failed save doesn't LOOK saved
      setDetEdits((s) => {
        const n = {};
        Object.entries(s).forEach(([k, v]) => { if (!k.startsWith(`${r.id}:`)) n[k] = v; });
        return n;
      });
      toast.error(formatApiError(err.response?.data?.detail) || `Could not save the ${label.toLowerCase()}`);
    }
  };

  const summarizeMembers = (d) => {
    const bits = [];
    if (d.created) bits.push(`${d.created} member${d.created === 1 ? "" : "s"} created`);
    if (d.already) bits.push(`${d.already} already had account${d.already === 1 ? "" : "s"} (tiles linked)`);
    if (d.incomplete) bits.push(`${d.incomplete} skipped — missing fields`);
    const mails = (d.emailed || 0) + (d.email_queued || 0);
    if (mails) bits.push(`${mails} invite email${mails === 1 ? "" : "s"} ${d.email_queued ? "sending now" : "sent"}`);
    return bits.join(" · ") || "Nothing to do";
  };

  const makeAllMembers = async () => {
    const n = memberableEmails.length;
    if (n === 0) {
      toast.error(`No emails are ready — ${blockedEmailCount} email${blockedEmailCount === 1 ? " is" : "s are"} missing required fields (name, CaryPact link, affiliate name, country, region).`);
      return;
    }
    const skipNote = blockedEmailCount ? `\n\n${blockedEmailCount} email${blockedEmailCount === 1 ? "" : "s"} will be SKIPPED — missing required fields (all columns are mandatory).` : "";
    if (!window.confirm(`Make members of ${n} unique email${n === 1 ? "" : "s"}?\n\nEach becomes a Member (status Active, training bypassed) named after their wallet, every wallet with that email links to the account, and they get an invite email to set their password. Emails that already have an account are left untouched.${skipNote}`)) return;
    setMakingMembers(true);
    try {
      const { data: res } = await api.post("/org-nodes/make-members", { origin: window.location.origin });
      toast.success(summarizeMembers(res));
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create members");
    } finally { setMakingMembers(false); }
  };

  const makeMember = async (r) => {
    setMakingId(r.id);
    try {
      const { data: res } = await api.post("/org-nodes/make-members", { emails: [r.email], origin: window.location.origin });
      const one = (res.results || [])[0];
      if (one?.status === "incomplete") {
        const miss = Array.isArray(one.missing) ? one.missing : Object.values(one.missing || {}).flat();
        toast.error(`All fields are mandatory — ${r.email} is still missing: ${miss.join(", ")}. Fill them on any of their wallet rows.`);
        return;
      }
      toast.success(one?.status === "already"
        ? `${r.email} already has an account — ${one.linked} wallet tile${one.linked === 1 ? "" : "s"} linked`
        : `Member created for ${r.email} — invite email sent to set their password`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create the member");
    } finally { setMakingId(null); }
  };

  const dropOn = async (target) => {
    const src = rows.find((r) => r.id === dragId);
    setDragId(null); setDropId(null);
    if (!src || src.id === target.id) return;
    if (src.pk !== target.pk) {
      toast.error("Same level only — drop it on a wallet with the same upline.");
      return;
    }
    const orig = rows.filter((r) => r.pk === src.pk).map((r) => r.id);
    const targetIdx = orig.indexOf(target.id);
    const next = orig.filter((id) => id !== src.id);
    next.splice(targetIdx, 0, src.id);
    setLocalOrder((s) => ({ ...s, [src.pk]: next }));
    try {
      await api.post("/org-nodes/reorder", { ordered_ids: next });
      toast.success("Order saved — the org chart follows this order now.");
      refresh();
    } catch (err) {
      setLocalOrder((s) => { const n = { ...s }; delete n[src.pk]; return n; });
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save the order.");
    }
  };

  // ---------- sync actions ----------
  const requestSync = async (r) => {
    setSending(r.node_id);
    try {
      const { data: res } = await api.post(`/wallet-sync/request/${r.node_id}`, { origin: window.location.origin });
      toast.success(`Sync request emailed to ${res.email}`);
      setOverview((prev) => prev ? { ...prev, nodes: { ...prev.nodes, [r.node_id]: { ...prev.nodes[r.node_id], requested_at: res.requested_at } } } : prev);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the email.");
    } finally { setSending(null); }
  };

  const bulkCandidates = useMemo(() => (data?.wallets || []).filter((w) => {
    const st = overview?.nodes?.[w.node_id]?.status || "never";
    return st === "never" || st === "expired";
  }), [data, overview]);

  const [bulkSending, setBulkSending] = useState(false);
  const scopeName = scope === "downline" ? "All wallets" : (portfolios.find((p) => p.id === scope)?.name || "this portfolio");

  const csvCell = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const exportCsv = (snap = null) => {
    const all = buildRows({ full: true });
    if (!all.length) { toast.info("Nothing to export yet"); return; }
    const header = ["Wallet name", "Level", "Reports to", "Wallet address", "Email", "Has login",
      "CaryPact link", "Affiliate name", "Country", "Region", "Rank", "Mining USD", "Staked USD",
      "Staked CA", "Power", "Direct reports", "Joined", "System sponsor", "Actual sponsor", "Income sync", "Last income sync"];
    const sw = snap?.wallets || null;
    const lines = all.map((r) => {
      const s = sw ? sw[(r.address || "").toLowerCase()] : null;
      const mining = s ? Number(s.mining_usd || 0) : Number(r.mining || 0);
      const staked = s ? Number(s.staked_usd || 0) : Number(r.staked || 0);
      const stakedCa = s ? Number(s.staked_ca || 0) : Number(r.stakedCa || 0);
      const power = s ? Number(s.power || 0) : Number(r.power || 0);
      return [
        r.unnamed ? "" : r.name, r.level, r.parentName, r.address, r.email, r.has_account ? "yes" : "no",
        r.link, r.affiliate, r.country, r.state, r.rank,
        mining.toFixed(2), staked.toFixed(2), stakedCa, power,
        r.directs || 0, r.joined, r.sysSponsor || "", r.actSponsorName || "",
        (STATUS_META[r.status] || STATUS_META.never).label, r.ov?.last_synced || "",
      ].map(csvCell).join(",");
    });
    const csv = "\uFEFF" + [header.map(csvCell).join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    const stamp = snap?.date || new Date().toISOString().slice(0, 10);
    a.download = `wallets_${scopeName.replace(/\s+/g, "-").toLowerCase()}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(snap ? `Exported ${all.length} wallets as of ${snap.date}` : `Exported ${all.length} wallets to CSV`);
  };

  const exportSnapshot = () => {
    if (!snapDate) { toast.info("Pick a date first"); return; }
    if (!snapData || snapData.date !== snapDate) { toast.info("Snapshot still loading — try again in a moment"); return; }
    exportCsv(snapData); // synchronous — data is already in hand
    setShowSnap(false);
  };
  const requestSyncBulk = async () => {
    if (!bulkCandidates.length) return;
    const withEmail = bulkCandidates.filter((w) => overview?.nodes?.[w.node_id]?.email).length;
    if (!window.confirm(`Email a one-tap sync request to every not-synced wallet in "${scopeName}"?\n\n${bulkCandidates.length} not synced · ${withEmail} have an email on file (only those get emailed).`)) return;
    setBulkSending(true);
    try {
      const { data: res } = await api.post("/wallet-sync/request-bulk", {
        node_ids: bulkCandidates.map((w) => w.node_id), origin: window.location.origin,
      });
      const bits = [`${res.sent} sent`];
      if (res.no_email) bits.push(`${res.no_email} skipped (no email)`);
      if (res.cooldown) bits.push(`${res.cooldown} on cooldown`);
      if (res.failed) bits.push(`${res.failed} failed`);
      (res.sent ? toast.success : toast.info)(`Bulk sync request: ${bits.join(" · ")}`, { duration: 8000 });
      api.get("/wallet-sync/overview").then((r) => setOverview(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk send failed.");
    } finally { setBulkSending(false); }
  };

  if (notLinked) {
    return (
      <div className="min-h-screen pb-16">
        <Header />
        <div className="max-w-[900px] mx-auto px-6 pt-16 text-center glass-strong p-10 mt-10" data-testid="wallets-not-linked">
          <Network size={28} className="mx-auto mb-3 text-ink-3" />
          <div className="font-display text-xl font-semibold">You're not linked to a team yet.</div>
          <p className="text-sm text-ink-2 mt-2">Once your admin links your account to a wallet on the org chart, your synced wallets appear here automatically.</p>
        </div>
        <HelpFab />
      </div>
    );
  }

  const shownCols = COLS.filter((c) => visibleCols.has(c.k));
  const inputBorder = "1px solid rgba(15,18,38,0.12)";

  const renderCell = (r, k) => {
    switch (k) {
      case "level":
        return <span className="font-mono text-[11px] text-ink-3">{r.level}</span>;
      case "parent":
        return r.parentName
          ? (isAddrLabel(r.parentName)
            ? <span className="font-mono text-[11px] text-ink-3">{shortAddr(r.parentName)}</span>
            : <span className="text-[12px] text-ink-2 block max-w-[130px] truncate" title={r.parentName}>{r.parentName}</span>)
          : <span className="text-ink-3 text-[12px]">— top —</span>;
      case "wallet":
        return (
          <button onClick={() => copyText(r.address, "Address copied")} className="inline-flex items-center gap-1.5 font-mono text-[12px] text-ink-2 hover:text-ink" title={`${r.address} — click to copy`}>
            {shortAddr(r.address)} <Copy size={11} className="text-ink-3" />
          </button>
        );
      case "email": {
        const cur = emailEdits[r.id] ?? r.email;
        const sug = isAdmin && !cur ? emailSuggest[(r.address || "").toLowerCase()] : null;
        return isAdmin ? (
          <div className="flex flex-col gap-1" style={{ minWidth: 140 }}>
            <div className="flex items-center gap-1.5">
              <input
                value={emailEdits[r.id] ?? r.email}
                onChange={(e) => setEmailEdits((s) => ({ ...s, [r.id]: e.target.value }))}
                onBlur={(e) => saveEmail(r, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                placeholder="e.g. name@email.com"
                className="w-full h-7 px-2 rounded-md font-mono text-[11px] outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF]"
                style={{
                  border: `1px solid ${(emailEdits[r.id] ?? r.email) ? "rgba(15,18,38,0.12)" : "rgba(245,158,11,0.55)"}`,
                  background: (emailEdits[r.id] ?? r.email) ? "white" : "rgba(245,158,11,0.06)",
                  minWidth: 130,
                }}
                title={r.has_account ? "Linked to an account" : r.email ? "Email attached — no account yet" : "Orphan — type an email to attach it"}
                data-testid={`wallets-email-input-${r.id}`}
              />
              {r.has_account ? (
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#00863A" }} title="Linked to an account" data-testid={`wallets-linked-dot-${r.id}`} />
              ) : (
                <button onClick={() => makeMember(r)} disabled={makingId === r.id || !r.email}
                  className="shrink-0 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ color: !r.email || emailMissing.get((r.email || "").toLowerCase()) ? "#9AA0B8" : "#00863A" }}
                  title={!r.email
                    ? "Add an email on this wallet first — then you can make them a member from here"
                    : emailMissing.get((r.email || "").toLowerCase())
                      ? `All fields are mandatory before making a member — missing: ${emailMissing.get((r.email || "").toLowerCase()).join(", ")}`
                      : `Make a Member — create an account for ${r.email} (Active, training bypassed) + invite email to set their password`}
                  data-testid={`wallets-make-member-${r.id}`}>
                  <UserPlus size={13} />
                </button>
              )}
            </div>
            {sug && (
              <button onClick={() => saveEmail(r, sug.email)}
                className="text-[10px] text-left text-blue-700 hover:underline inline-flex items-center gap-1 leading-tight"
                title={`We captured ${sug.email}${sug.name ? ` (${sug.name})` : ""} during their onboarding — click to attach it to this wallet`}
                data-testid={`wallets-email-suggest-${r.id}`}>
                <Mail size={10} className="shrink-0" /> <span className="truncate max-w-[150px]">Use {sug.email}</span>
              </button>
            )}
          </div>
        ) : (
          <span className="font-mono text-[11px] text-ink-2">{r.email || "—"}</span>
        );
      }
      case "link":
        return isAdmin ? (
          <input
            value={detEdits[dkey(r.id, "link")] ?? r.link}
            onChange={(e) => setDetEdits((s) => ({ ...s, [dkey(r.id, "link")]: e.target.value }))}
            onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.link) saveDetail(r, { invite_link: v }, "CaryPact link"); }}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            placeholder="Paste invite link…"
            className="w-full h-7 px-2 rounded-md font-mono text-[11px] outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF]"
            style={{
              border: inputBorder,
              background: r.linkSynced && !(dkey(r.id, "link") in detEdits) ? "rgba(64,96,255,0.04)" : "white",
              minWidth: 125,
            }}
            title={(detEdits[dkey(r.id, "link")] ?? r.link) || "Paste this wallet's CaryPact invite link (app.carypact.com/?inviteCode=…)"}
            data-testid={`wallets-link-input-${r.id}`}
          />
        ) : (
          r.link
            ? <a href={r.link} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-[#4060FF] underline underline-offset-2 block max-w-[160px] truncate" title={r.link}>{r.link}</a>
            : <span className="text-ink-3 text-[12px]">—</span>
        );
      case "affiliate":
        return isAdmin ? (
          <input
            value={detEdits[dkey(r.id, "affiliate")] ?? r.affiliate}
            onChange={(e) => setDetEdits((s) => ({ ...s, [dkey(r.id, "affiliate")]: e.target.value }))}
            onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.affiliate) saveDetail(r, { affiliate_name: v }, "Affiliate name"); }}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            placeholder="Invite code…"
            className="w-full h-7 px-2 rounded-md text-[12px] outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF]"
            style={{ border: inputBorder, minWidth: 85 }}
            title="Public 'Invited by' name — defaults to the invite code from the CaryPact link"
            data-testid={`wallets-affiliate-input-${r.id}`}
          />
        ) : (
          <span className="text-[12px] text-ink-2">{r.affiliate || "—"}</span>
        );
      case "country": {
        const cVal = detEdits[dkey(r.id, "country")] ?? r.country;
        return isAdmin ? (
          <select
            value={cVal}
            onChange={(e) => {
              const v = e.target.value;
              const sVal = detEdits[dkey(r.id, "state")] ?? r.state;
              const keepState = v === r.country;
              setDetEdits((s) => ({ ...s, [dkey(r.id, "country")]: v, [dkey(r.id, "state")]: keepState ? sVal : "" }));
              saveDetail(r, { country: v, state: keepState ? sVal : "" }, "Country");
            }}
            className="w-full h-7 px-1.5 rounded-md text-[12px] outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF] bg-white"
            style={{ border: inputBorder, minWidth: 105, color: cVal ? undefined : "#9AA0B8" }}
            data-testid={`wallets-country-select-${r.id}`}
          >
            <option value="">— country —</option>
            {cVal && !COUNTRY_OPTS.includes(cVal) && <option value={cVal}>{cVal} (existing)</option>}
            {COUNTRY_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <span className="text-[12px] text-ink-2">{r.country || "—"}</span>
        );
      }
      case "region": {
        const cVal = detEdits[dkey(r.id, "country")] ?? r.country;
        const sVal = detEdits[dkey(r.id, "state")] ?? r.state;
        const states = getStatesFor(cVal);
        return isAdmin ? (
          <select
            value={sVal}
            disabled={!cVal}
            onChange={(e) => {
              const v = e.target.value;
              setDetEdits((s) => ({ ...s, [dkey(r.id, "state")]: v }));
              saveDetail(r, { state: v }, "Region");
            }}
            className="w-full h-7 px-1.5 rounded-md text-[12px] outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF] bg-white disabled:opacity-50"
            style={{ border: inputBorder, minWidth: 105, color: sVal ? undefined : "#9AA0B8" }}
            data-testid={`wallets-region-select-${r.id}`}
          >
            <option value="">{cVal ? "— region —" : "country first"}</option>
            {sVal && !states.includes(sVal) && <option value={sVal}>{sVal} (existing)</option>}
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <span className="text-[12px] text-ink-2">{r.state || "—"}</span>
        );
      }
      case "rank":
        return r.rank ? <span className="chip text-[11px]" style={{ background: "rgba(217,119,6,0.12)", color: "#B45309", fontWeight: 800 }}>{r.rank}</span> : <span className="text-ink-3 text-[12px]">—</span>;
      case "mining":
        return <span className="font-mono text-xs tabular-nums">{usd(r.mining)}</span>;
      case "staked":
        // CA staking + CaryPact's BOT pledge, the two assets that make up a wallet's
        // staking value. The hover says which is which, because they're different money.
        return (
          <span className="font-mono text-xs tabular-nums" data-testid={`wallet-staked-${r.node_id}`}
            title={r.stakedBot > 0
              ? `${r.stakedCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA staked + ${r.stakedBot.toLocaleString(undefined, { maximumFractionDigits: 4 })} BOT pledged`
              : `${r.stakedCa.toLocaleString()} CA`}>
            {usd(r.staked)}
            {r.stakedBot > 0 && <span className="ml-1 text-[9px] font-bold" style={{ color: "#0E7490" }}>+BOT</span>}
          </span>
        );
      case "power":
        return <span className="font-mono text-xs tabular-nums">{Number(r.power || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>;
      case "direct":
        return <span className="font-mono text-xs tabular-nums text-ink-3">{r.directs || ""}</span>;
      case "joined":
        return <span className="font-mono text-[11px] text-ink-3 whitespace-nowrap">{r.joined || "—"}</span>;
      case "sys_sponsor":
        return r.sysSponsor ? (
          <span className="text-[12px] text-ink-2" title={r.sysSponsorVia === "upline" ? "One level up in the CaryPact tree" : `From onboarding · ${r.sysSponsorVia}`} data-testid={`wallets-sys-sponsor-${r.id}`}>{r.sysSponsor}</span>
        ) : <span className="text-ink-3 text-[12px]" data-testid={`wallets-sys-sponsor-${r.id}`}>—</span>;
      case "act_sponsor": {
        const cur = detEdits[dkey(r.id, "act_sponsor")] ?? r.actSponsorId;
        return isAdmin ? (
          <select
            value={cur}
            onChange={(e) => {
              const v = e.target.value;
              setDetEdits((s) => ({ ...s, [dkey(r.id, "act_sponsor")]: v }));
              saveDetail(r, { actual_sponsor_user_id: v }, "Actual sponsor");
            }}
            className="w-full h-7 px-1.5 rounded-md text-[12px] outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED] bg-white"
            style={{ border: inputBorder, minWidth: 150, color: cur ? undefined : "#6E6889" }}
            data-testid={`wallets-act-sponsor-select-${r.id}`}
            title="Defaults to the system sponsor (one level up) — change it only when someone else actually referred this person"
          >
            <option value="">{r.sysSponsor ? `Same as system · ${r.sysSponsor}` : "— who referred? —"}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
          </select>
        ) : <span className="text-[12px] text-ink-2">{r.actSponsorName || "—"}</span>;
      }
      case "share": {
        const code = r.cp?.promotionCode || codeFrom(r.link || "");
        return code ? (
          <button
            onClick={() => copyWalletLink(r)}
            title={`Copy ${r.name || "this wallet"}'s invite link — leads who click it are tagged to this wallet`}
            data-testid={`wallets-copy-invite-${r.id}`}
            className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md font-mono text-[11px] transition-colors"
            style={{ border: inputBorder, background: "white" }}
          >
            <Copy size={11} className="text-[#4060FF]" /> {code}
          </button>
        ) : <span className="text-ink-3 text-[12px]" title="Add this wallet's CaryPact link first">—</span>;
      }
      case "sync":
        return (
          <div>
            <StatusChip status={r.status} />
            {r.ov.last_synced && <div className="text-[10px] font-mono text-ink-3 mt-0.5">synced {String(r.ov.last_synced).slice(0, 16).replace("T", " ")} UTC</div>}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pb-8" data-testid="wallets-page">
      <Header />
      <div className={`max-w-[1600px] mx-auto px-6 md:px-10 ${focusMode ? "pt-3" : "pt-4"}`}>

        {!focusMode && (
          <div className="relative z-40 flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-3 fade-up">
            <div>
              <div className="pill mb-2"><span className="dot" />Wallets · everything in one place</div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Wallets.</h1>
              <p className="text-ink-2 mt-1 text-sm max-w-2xl">
                <Zap size={13} className="inline align-[-2px] mr-1" style={{ color: "#00863A" }} />
                Balances sync automatically for everyone — real <b>income</b> needs each wallet&apos;s one-tap sync.
              </p>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <div className="text-[10px] font-bold tracking-widest mb-1 ml-0.5" style={{ color: "#94A3B8" }} data-testid="wallets-portfolio-label">SELECT A PORTFOLIO</div>
                <select value={scope} onChange={(e) => setScope(e.target.value)} className="input-glass !py-2.5 text-sm font-semibold cursor-pointer" data-testid="wallets-portfolio-select" title="Show only one portfolio's wallets so they're quick to sync">
                  <option value="downline">All wallets</option>
                  {portfolios.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({(p.wallet_addresses || []).length})</option>
                  ))}
                </select>
              </div>
              {isAdmin && (
                <button onClick={() => setShowSyncSettings(true)} className="btn-secondary" data-testid="wallets-sync-settings-btn" title="Daily sync schedule, CaryPact connection and full sync">
                  <SlidersHorizontal size={14} /> Sync settings
                </button>
              )}
              <button onClick={refresh} className="btn-secondary" data-testid="wallets-refresh-btn"><RefreshCw size={14} /> Refresh</button>
              <button onClick={() => exportCsv()} className="btn-secondary" data-testid="wallets-export-csv-btn" title="Download the full wallet table (every column, collapsed branches included) as a spreadsheet">
                <Download size={14} /> Export CSV
              </button>
              {isAdmin && snapDates.length > 0 && (
                <div className="relative z-50">
                  <button onClick={() => setShowSnap((v) => !v)} className="btn-secondary" data-testid="wallets-snapshot-btn"
                    title="Export the table with figures exactly as they were on a past date">
                    <CalendarClock size={14} /> CSV as of date
                  </button>
                  {showSnap && (
                    <div className="absolute right-0 mt-2 z-50 w-64 p-3 rounded-xl bg-white border shadow-xl" style={{ borderColor: "rgba(15,18,38,0.10)" }} data-testid="wallets-snapshot-panel">
                      <p className="text-[11px] text-ink-3 mb-2 leading-snug">Mining / staking / power figures as of the chosen day. Names &amp; structure stay current.</p>
                      <select value={snapDate} onChange={(e) => setSnapDate(e.target.value)}
                        className="w-full text-sm border rounded-lg px-2 py-1.5 mb-2 bg-white" style={{ borderColor: "rgba(15,18,38,0.12)" }}
                        data-testid="wallets-snapshot-date-select">
                        {[...snapDates].reverse().map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <button onClick={exportSnapshot} disabled={snapBusy || !(snapData && snapData.date === snapDate)} className="btn-primary w-full justify-center text-sm disabled:opacity-50" data-testid="wallets-snapshot-export-btn">
                        {snapBusy ? "Loading…" : `Export ${snapDate}`}
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button onClick={toggleFocus} className="btn-secondary" data-testid="wallets-hero-focus-btn" title="Hide the header and KPIs so the table fills the screen">
                <Maximize2 size={14} /> Focus table
              </button>
            </div>
          </div>
        )}

        {error && <div className="chip chip-amber mb-2" data-testid="wallets-error">{error}</div>}

        {!data ? (
          <div className="text-sm text-ink-3 font-mono py-16 text-center" data-testid="wallets-loading">Loading synced wallets…</div>
        ) : (
          <>
            {!focusMode && (overview?.portfolios?.length > 0) && (
              <div className="glass-strong px-3 py-2 mb-2 fade-up" style={{ borderRadius: 12 }} data-testid="wallets-portfolio-progress">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="kpi-label" style={{ fontSize: 10, marginBottom: 0 }}>Portfolio sync progress</div>
                  <div className="text-[10px] font-mono text-ink-3">click a bar to filter</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-3 gap-y-1.5">
                  {[{ id: "downline", name: "All wallets", total: overview.summary?.total || 0, synced: (overview.summary?.synced || 0) + (overview.summary?.connected || 0) }, ...overview.portfolios].map((p) => {
                    const pct = p.total ? Math.round((p.synced / p.total) * 100) : 0;
                    const active = scope === p.id;
                    return (
                      <button key={p.id} onClick={() => setScope(p.id)} className="text-left group min-w-0" data-testid={`portfolio-progress-${p.id}`}>
                        <div className="flex items-baseline justify-between mb-0.5 min-w-0 gap-1.5">
                          <span className={`text-[11px] font-semibold truncate ${active ? "text-ink" : "text-ink-2 group-hover:text-ink"}`} title={p.name}>{p.name}</span>
                          <span className="text-[10px] font-mono text-ink-3 flex-shrink-0">{p.synced}/{p.total} · {pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.07)" }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: pct === 100 ? "#00863A" : "linear-gradient(90deg, #4060FF, #6B8AFF)", minWidth: p.synced ? 6 : 0 }} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!focusMode && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2" data-testid="kpi-grid">
                <div className="kpi-tile" style={{ padding: "10px 12px", borderRadius: 12 }} data-testid="kpi-wallets-count"><div className="kpi-label" style={{ fontSize: 10, marginBottom: 2 }}>Wallets</div><div className="kpi-num" style={{ fontSize: 16 }}>{t.wallets ?? 0}</div></div>
                <div className="kpi-tile" style={{ padding: "10px 12px", borderRadius: 12 }} data-testid="kpi-wallets-income-synced"><div className="kpi-label" style={{ fontSize: 10, marginBottom: 2 }}>Income synced</div><div className="kpi-num" style={{ fontSize: 16, color: syncedCount ? "#00863A" : undefined }}>{syncedCount}<span className="text-ink-3 text-[11px] font-normal"> / {sum.total ?? t.wallets ?? 0}</span></div></div>
                <div className="kpi-tile" style={{ padding: "10px 12px", borderRadius: 12 }} data-testid="kpi-wallets-locked"><div className="kpi-label" style={{ fontSize: 10, marginBottom: 2 }}>Mining locked</div><div className="kpi-num" style={{ fontSize: 16 }}>{usd(t.mining_locked_usd)}</div></div>
                <div className="kpi-tile" style={{ padding: "10px 12px", borderRadius: 12 }} data-testid="kpi-wallets-staked"><div className="kpi-label" style={{ fontSize: 10, marginBottom: 2 }}>Staked</div><div className="kpi-num" style={{ fontSize: 16 }} title={`${Number(t.staked_ca || 0).toLocaleString()} CA`}>{usd(t.staked_usd)}</div></div>
                <div className="kpi-tile" style={{ padding: "10px 12px", borderRadius: 12 }} data-testid="kpi-wallets-revenue"><div className="kpi-label" style={{ fontSize: 10, marginBottom: 2 }}>Power</div><div className="kpi-num" style={{ fontSize: 16 }}>{Number(t.power || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
                <div className="kpi-tile" style={{ padding: "10px 12px", borderRadius: 12 }} data-testid="kpi-wallets-sync"><div className="kpi-label" style={{ fontSize: 10, marginBottom: 2 }}>Last tree sync</div><div className="kpi-num" style={{ fontSize: 13 }}>{lastSync || "—"}</div></div>
              </div>
            )}

            <div className="glass-strong p-4 fade-up" data-testid="wallets-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(64,96,255,0.14), rgba(107,138,255,0.08))" }}><WalletIcon size={18} style={{ color: "#4060FF" }} /></div>
                  <div>
                    <div className="kpi-label">Everything about your wallets</div>
                    <h3 className="font-display text-xl font-semibold tracking-tight">Wallets {rows ? `(${rows.length})` : ""}</h3>
                    {isAdmin && (
                      <p className="text-[11px] text-ink-3 mt-0.5">
                        Click a header to sort · edit any field inline (saves on blur) · name a wallet <b>UNUSED</b> to hide its branch from the chart · drag <GripVertical size={10} className="inline -mt-0.5" /> to reorder same-level wallets.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={toggleFocus}
                    className="inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-2 transition-colors"
                    style={{ background: focusMode ? "rgba(64,96,255,0.10)" : "rgba(15,18,38,0.05)", color: focusMode ? "#4060FF" : "#5A607F" }}
                    title={focusMode ? "Show the page header + KPIs + portfolio progress" : "Hide the header and KPIs so the table fills the screen"}
                    data-testid="wallets-focus-toggle">
                    {focusMode ? <Minimize2 size={12} /> : <Maximize2 size={12} />} {focusMode ? "Exit focus" : "Focus table"}
                  </button>
                  {focusMode && (
                    <>
                      <select value={scope} onChange={(e) => setScope(e.target.value)}
                        className="input-glass !py-1.5 !px-2 text-xs font-semibold cursor-pointer"
                        style={{ maxWidth: 200 }}
                        data-testid="wallets-focus-portfolio-select"
                        title="Show only one portfolio's wallets so they're quick to sync">
                        <option value="downline">All wallets</option>
                        {portfolios.map((p) => (
                          <option key={p.id} value={p.id}>{p.name} ({(p.wallet_addresses || []).length})</option>
                        ))}
                      </select>
                      <button onClick={refresh}
                        className="inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-2 transition-colors"
                        style={{ background: "rgba(15,18,38,0.05)", color: "#5A607F" }}
                        title="Reload wallet data"
                        data-testid="wallets-focus-refresh-btn">
                        <RefreshCw size={12} /> Refresh
                      </button>
                      {isAdmin && (
                        <button onClick={() => setShowSyncSettings(true)}
                          className="inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-2 transition-colors"
                          style={{ background: "rgba(15,18,38,0.05)", color: "#5A607F" }}
                          title="Daily sync schedule, CaryPact token and full sync"
                          data-testid="wallets-focus-sync-settings-btn">
                          <SlidersHorizontal size={12} /> Sync settings
                        </button>
                      )}
                    </>
                  )}
                  {isAdmin && (memberableEmails.length > 0 || blockedEmailCount > 0) && (
                    <button onClick={makeAllMembers} disabled={makingMembers}
                      className="inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-2 disabled:opacity-40 transition-colors"
                      style={memberableEmails.length ? { background: "rgba(0,134,58,0.10)", color: "#00863A" } : { background: "rgba(15,18,38,0.06)", color: "#5A607F" }}
                      title={`Create Member accounts (Active, training bypassed) for every unique email with ALL fields filled — each gets an invite email to set their password.${blockedEmailCount ? ` ${blockedEmailCount} email${blockedEmailCount === 1 ? "" : "s"} blocked: every column (name, CaryPact link, affiliate name, country, region) is mandatory.` : ""}`}
                      data-testid="wallets-make-members-btn">
                      <UserPlus size={12} /> {makingMembers ? "Creating members…" : `Make members (${memberableEmails.length}${blockedEmailCount ? ` · ${blockedEmailCount} blocked` : ""})`}
                    </button>
                  )}
                  <button onClick={requestSyncBulk} disabled={bulkSending || bulkCandidates.length === 0}
                    className="inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-2 disabled:opacity-40 transition-colors"
                    style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }}
                    title={`Email a one-tap sync link to every not-synced wallet in ${scopeName} that has an email on file`}
                    data-testid="wallets-bulk-sync-btn">
                    {bulkSending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                    Email all not-synced ({bulkCandidates.length})
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-2 transition-colors"
                        style={{ background: "rgba(15,18,38,0.05)", color: "#5A607F" }}
                        title="Choose which columns to show" data-testid="wallets-columns-btn">
                        <Columns3 size={12} /> Columns ({shownCols.length + 2}/{COLS.length + 2})
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 max-h-[70vh] overflow-y-auto">
                      <DropdownMenuLabel className="text-[11px]">Visible columns</DropdownMenuLabel>
                      <DropdownMenuCheckboxItem checked disabled className="text-[12px] opacity-60">Wallet name (always)</DropdownMenuCheckboxItem>
                      {COLS.map((c) => (
                        <DropdownMenuCheckboxItem key={c.k} checked={visibleCols.has(c.k)}
                          onCheckedChange={() => toggleCol(c.k)}
                          onSelect={(e) => e.preventDefault()}
                          className="text-[12px]" data-testid={`wallets-col-${c.k}`}>
                          {c.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem checked disabled className="text-[12px] opacity-60">Action (always)</DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <div className="inline-flex gap-1 p-1 rounded-xl" style={{ background: "rgba(15,18,38,0.05)" }} data-testid="wallets-status-filter">
                    {[
                      { key: "all", label: `All${sum.total ? ` (${sum.total})` : ""}` },
                      { key: "synced", label: `Synced (${syncedCount})` },
                      { key: "expired", label: `Reconnect (${sum.expired || 0})` },
                      { key: "never", label: `Not synced (${sum.never || 0})` },
                    ].map((f) => (
                      <button key={f.key} onClick={() => setStatusFilter(f.key)} data-testid={`wallets-filter-${f.key}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${statusFilter === f.key ? "bg-white text-ink shadow-sm" : "text-ink-2 hover:bg-white/50"}`}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <div className="relative w-full sm:w-72">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: query ? "#4060FF" : "#6E6889" }} />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name · address · email…"
                      className="input-glass w-full !pl-9 !pr-8 font-semibold transition-shadow"
                      style={{
                        background: "#fff",
                        border: `1.5px solid ${query ? "#4060FF" : "rgba(64,96,255,0.35)"}`,
                        boxShadow: query ? "0 0 0 4px rgba(64,96,255,0.16)" : "0 1px 3px rgba(64,96,255,0.10)",
                      }}
                      data-testid="wallets-search" />
                    {query && (
                      <button onClick={() => setQuery("")} title="Clear search" data-testid="wallets-search-clear"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink">
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-3">
                {sort && (
                  <button onClick={() => setSort(null)} className="h-7 px-3 rounded-md text-[12px] font-semibold" style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }} data-testid="wallets-clear-sort">
                    Sorted by {(COLS.find((c) => c.k === sort.key) || { label: "Wallet name" }).label} {sort.dir === 1 ? "↑" : "↓"} · clear (back to tree order)
                  </button>
                )}
                {pureTree && (
                  <div className="inline-flex items-center gap-1.5">
                    <span className="text-[11px] font-mono uppercase tracking-wider text-ink-3">Levels</span>
                    <div className="inline-flex gap-0.5 p-0.5 rounded-lg" style={{ background: "rgba(15,18,38,0.05)" }}>
                      {[1, 2, 3, "all"].map((n) => (
                        <button key={n} onClick={() => applyLevel(n)}
                          className="h-7 px-2.5 rounded-md text-[12px] font-semibold transition-all"
                          style={levelView === n ? { background: "white", color: "#0F1226", boxShadow: "0 1px 4px rgba(15,18,38,0.12)" } : { color: "#5A607F" }}
                          title={n === "all" ? "Expand everything" : `Show the top ${n} level${n === 1 ? "" : "s"} only`}
                          data-testid={`wallets-level-${n}`}>
                          {n === "all" ? "All" : `L${n}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {rows.length === 0 ? (
                <div className="text-sm text-ink-3 font-mono py-8 text-center" data-testid="wallets-empty">
                  {query || statusFilter !== "all" ? "No wallets match." : "No synced wallets yet — connect your CaryPact tree from My Community → CaryPact history."}
                </div>
              ) : (
                <>
                <WalletsPagination
                  page={page} pageCount={pageCount} pageSize={pageSize}
                  total={rows.length} onPage={setPage} onPageSize={savePageSize}
                />
                <div className="overflow-x-auto" style={{ zoom: 0.75 }}>
                  <table className="w-full text-sm" data-testid="wallets-table">
                    <thead>
                      <tr className="text-left">
                        {canReorder && <th className="py-2 pr-1 w-[24px]"></th>}
                        <th className="py-2 pr-3 font-mono text-[10px] uppercase tracking-wider text-ink-3 whitespace-nowrap">Action</th>
                        <th className="py-2 pr-3">
                          <button onClick={() => clickSort("name")}
                            className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors hover:text-ink ${sort?.key === "name" ? "" : "text-ink-3"}`}
                            style={sort?.key === "name" ? { color: "#4060FF" } : undefined}
                            title="Sort by Wallet name" data-testid="wallets-sort-name">
                            Wallet name
                            {sort?.key === "name" ? (sort.dir === 1 ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ChevronsUpDown size={10} className="opacity-40" />}
                          </button>
                        </th>
                        {shownCols.map((c) => (
                          <th key={c.k} className={`py-2 pr-3 ${c.right ? "text-right" : "text-left"}`}>
                            <button onClick={() => clickSort(c.k)}
                              className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors hover:text-ink ${sort?.key === c.k ? "" : "text-ink-3"}`}
                              style={sort?.key === c.k ? { color: "#4060FF" } : undefined}
                              title={`Sort by ${c.label}`} data-testid={`wallets-sort-${c.k}`}>
                              {c.label}
                              {sort?.key === c.k ? (sort.dir === 1 ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ChevronsUpDown size={10} className="opacity-40" />}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pagedRows.map((r) => {
                        const needsSync = r.status === "never" || r.status === "expired";
                        return (
                          <tr key={r.id}
                            onDragOver={(e) => { if (dragId && canReorder) { e.preventDefault(); setDropId(r.id); } }}
                            onDragLeave={() => setDropId((d) => (d === r.id ? null : d))}
                            onDrop={(e) => { e.preventDefault(); if (dragId && canReorder) dropOn(r); }}
                            className="border-t"
                            style={{
                              borderColor: "rgba(15,18,38,0.06)",
                              background: dropId === r.id && dragId && dragId !== r.id ? "rgba(64,96,255,0.08)" : "transparent",
                              opacity: dragId === r.id ? 0.45 : 1,
                            }}
                            data-testid={`wallet-row-${r.node_id}`}>
                            {canReorder && (
                              <td className="py-1.5 pr-1">
                                <span
                                  draggable
                                  onDragStart={(e) => { setDragId(r.id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", r.id); } catch { /* older browsers */ } }}
                                  onDragEnd={() => { setDragId(null); setDropId(null); }}
                                  className="cursor-grab text-ink-3 hover:text-ink inline-flex"
                                  title="Drag onto another wallet at the same level to reorder the chart"
                                  data-testid={`wallets-drag-${r.id}`}
                                >
                                  <GripVertical size={13} />
                                </span>
                              </td>
                            )}
                            <td className="py-1.5 pr-3 align-middle">
                              <div className="flex items-center gap-1.5">
                                {needsSync && (
                                  r.ov.email ? (
                                    <button onClick={() => requestSync(r)} disabled={sending === r.node_id}
                                      className="inline-flex items-center gap-1 text-[10px] font-bold rounded-md px-1.5 py-1 transition-colors whitespace-nowrap"
                                      style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }}
                                      title={`Email ${r.ov.email} a one-tap sync link`} data-testid={`wallet-sync-request-${r.node_id}`}>
                                      {sending === r.node_id ? <Loader2 size={10} className="animate-spin" /> : <Mail size={10} />}
                                      {r.ov.requested_at ? "Re-send" : "Email"}
                                    </button>
                                  ) : (
                                    <span className="text-[10px] text-ink-3 whitespace-nowrap" title="Add an email in the Email column to enable email requests">no email</span>
                                  )
                                )}
                                <button onClick={() => setSyncModal({ node_id: r.node_id, address: r.address, name: r.name })}
                                  className="inline-flex items-center gap-1 text-[10px] font-bold rounded-md px-1.5 py-1 transition-colors whitespace-nowrap"
                                  style={{ background: needsSync ? "rgba(0,134,58,0.10)" : "rgba(15,18,38,0.05)", color: needsSync ? "#00863A" : "#5A607F" }}
                                  title="Sync this wallet right here — connect TokenPocket in a popup" data-testid={`wallet-sync-open-${r.node_id}`}>
                                  <Zap size={10} /> {needsSync ? "Sync" : "Re-sync"}
                                </button>
                              </div>
                              {needsSync && r.ov.requested_at && <div className="text-[10px] font-mono text-ink-3 mt-0.5">requested {String(r.ov.requested_at).slice(0, 10)}</div>}
                            </td>
                            <td className="py-1.5 pr-3">
                              <div className="flex items-center" style={{ paddingLeft: pureTree ? Math.min(r.level, 8) * 14 : 0 }}>
                                {pureTree && r.directs > 0 ? (
                                  <button
                                    onClick={() => toggleBranch(r.id)}
                                    className="mr-1 shrink-0 text-ink-3 hover:text-ink transition-colors"
                                    title={r.isCollapsed ? `Expand ${r.directs} direct${r.directs === 1 ? "" : "s"}` : "Collapse this branch"}
                                    data-testid={`wallets-collapse-${r.id}`}
                                  >
                                    <ChevronRight size={14} className={`transition-transform ${r.isCollapsed ? "" : "rotate-90"}`} />
                                  </button>
                                ) : (
                                  pureTree && r.level > 0 && <CornerDownRight size={11} className="text-ink-3 mr-1 shrink-0" />
                                )}
                                {isAdmin ? (
                                  <input
                                    value={nameEdits[r.id] ?? (r.unnamed ? "" : r.name)}
                                    onChange={(e) => setNameEdits((s) => ({ ...s, [r.id]: e.target.value }))}
                                    onBlur={(e) => saveName(r, e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                                    placeholder={r.unnamed ? "Type a name…" : r.name}
                                    className="w-full h-7 px-2 rounded-md text-[13px] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF]"
                                    style={{ border: inputBorder, minWidth: 120, background: "white" }}
                                    data-testid={`wallets-name-input-${r.id}`}
                                  />
                                ) : (
                                  <span className="font-semibold text-ink text-[13px]">{r.unnamed ? shortAddr(r.address) : r.name || "—"}</span>
                                )}
                                {pureTree && r.isCollapsed && r.directs > 0 && (
                                  <span className="ml-2 shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "rgba(64,96,255,0.08)", color: "#4060FF" }}>
                                    +{r.directs}
                                  </span>
                                )}
                              </div>
                            </td>
                            {shownCols.map((c) => (
                              <td key={c.k} className={`py-1.5 pr-3 ${c.right ? "text-right" : ""}`}>{renderCell(r, c.k)}</td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
      {syncModal && (
        <SyncNowModal
          wallet={syncModal}
          email={overview?.nodes?.[syncModal.node_id]?.email}
          onClose={() => setSyncModal(null)}
          onSynced={() => { setSyncModal(null); refresh(); }}
          onEmail={() => { const w = syncModal; setSyncModal(null); requestSync(w); }}
        />
      )}
      {showSyncSettings && (
        <SyncSettingsModal onClose={() => setShowSyncSettings(false)} onSynced={refresh} />
      )}
      <HelpFab />
    </div>
  );
}

function SyncNowModal({ wallet, email, onClose, onSynced, onEmail }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const link = `${window.location.origin}/sync?w=${wallet.address}`;

  const connect = async () => {
    setBusy(true); setError("");
    try {
      const data = await connectAndMintPublic(wallet.address);
      setDone(data);
      if (data.mismatch) {
        toast.success(`Synced ${shortAddr(data.address)} — the wallet selected in TokenPocket`, { duration: 7000 });
      } else {
        toast.success(`${wallet.name || shortAddr(wallet.address)} synced — history importing now`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not sync the wallet — try again.");
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Sync link copied"); } catch { /* blocked */ }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(10,12,28,0.55)" }} data-testid="wallet-sync-modal">
      <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid rgba(15,18,38,0.1)" }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.08)" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,134,58,0.12)" }}><Zap size={15} style={{ color: "#00863A" }} /></div>
            <div>
              <div className="font-semibold text-[15px]" style={{ color: "#0F1226" }}>Sync {wallet.name || "wallet"}</div>
              <div className="text-[11px] font-mono" style={{ color: "#64748B" }}>{shortAddr(wallet.address)}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5" data-testid="wallet-sync-modal-close"><X size={16} /></button>
        </div>
        <div className="px-5 py-5">
          {done ? (
            <div className="text-center py-3" data-testid="wallet-sync-modal-success">
              <CheckCircle2 size={36} className="mx-auto mb-2" style={{ color: "#00863A" }} />
              <div className="font-semibold" style={{ color: "#0F1226" }}>Synced!</div>
              <p className="text-[12.5px] mt-1.5" style={{ color: "#475569" }}>
                Wallet <b className="font-mono">{shortAddr(done.address)}</b> is connected — its full history is importing now.
              </p>
              {done.mismatch && (
                <div className="chip chip-gray mt-3 w-full !items-start text-left leading-relaxed" data-testid="wallet-sync-modal-mismatch">
                  <span>All done — the sync worked. The wallet requested from Wallets was <b className="font-mono">{shortAddr(done.expected)}</b>, and the wallet you synced from TokenPocket was <b className="font-mono">{shortAddr(done.address)}</b>. To sync {wallet.name || shortAddr(done.expected)} too, switch to it in TokenPocket and sync again.</span>
                </div>
              )}
              <button onClick={onSynced} className="btn-primary w-full justify-center mt-4" data-testid="wallet-sync-modal-done">Done</button>
            </div>
          ) : (
            <>
              <p className="text-[12.5px] leading-relaxed mb-4" style={{ color: "#475569" }}>
                If this wallet is in <b>your</b> TokenPocket / browser wallet, sync it right here — one tap, one signature.
                The signature only proves ownership; it can&apos;t move funds.
              </p>
              <button onClick={connect} disabled={busy} className="btn-primary w-full !py-3 justify-center" data-testid="wallet-sync-modal-connect">
                {busy ? <><Loader2 size={15} className="animate-spin" /> Waiting for your wallet…</> : <><Zap size={15} /> Connect &amp; sync this wallet</>}
              </button>
              <button onClick={() => { window.location.href = tokenPocketDeepLink(link); }} className="btn-secondary w-full !py-2.5 justify-center mt-2.5 text-[13px]" data-testid="wallet-sync-modal-open-tokenpocket" style={{ display: isMobileDevice() ? undefined : "none" }}>
                <ExternalLink size={14} /> Open in TokenPocket app
              </button>
              {error && <div className="chip chip-amber mt-3 w-full" data-testid="wallet-sync-modal-error">{error}</div>}
              <div className="flex items-center gap-2 my-4">
                <div className="flex-1 h-px" style={{ background: "rgba(15,18,38,0.08)" }} />
                <span className="text-[10px] font-bold tracking-wider" style={{ color: "#94A3B8" }}>NOT YOUR WALLET?</span>
                <div className="flex-1 h-px" style={{ background: "rgba(15,18,38,0.08)" }} />
              </div>
              <div className="flex items-center gap-2">
                {email ? (
                  <button onClick={onEmail} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[12px] font-bold rounded-xl px-3 py-2.5"
                    style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }} data-testid="wallet-sync-modal-email">
                    <Mail size={12} /> Email sync request
                  </button>
                ) : (
                  <span className="flex-1 text-center text-[11px]" style={{ color: "#94A3B8" }}>No email on file for this wallet</span>
                )}
                <button onClick={copyLink} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[12px] font-bold rounded-xl px-3 py-2.5"
                  style={{ background: "rgba(15,18,38,0.05)", color: "#5A607F" }} data-testid="wallet-sync-modal-copylink">
                  <LinkIcon size={12} /> Copy sync link
                </button>
              </div>
              <div className="flex items-start gap-1.5 mt-3 text-[10.5px]" style={{ color: "#94A3B8" }}>
                <ShieldCheck size={11} className="shrink-0 mt-0.5" /> The owner opens the link inside the TokenPocket browser and approves one signature — that&apos;s it.
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
